import React from 'react';
import Image from 'next/image';
import {
  FooterContainer,
  FooterContainerItem,
  FooterLogoContainer,
  FooterInnerContainer,
  FooterInnerContainerContent,
  SocialMediaSection,
  AddressContent,
  PrivacyAndPolicyContent,
  FooterExtLink,
  FooterContentBlocks,
  ContactDetailsItems,
} from './footer.styles';
import EnvelopeIcon from 'public/icons/envelope.svg';
import PhoneIcon from 'public/icons/phone.svg';
import { useTranslation } from 'react-i18next';
import { useCmsWithQuerySingle } from 'common/hooks/use-cms';
import { useUserStore } from 'common/stores/user-store';
import { CmsCollections, CmsFieldNames } from 'common/utils/constants/cms.constants';
import { FooterDetailsNode } from 'modules/dashboard/types/dashboard.types';
import { Loader } from '@sapiens/insured-component-library';
import { FlexBox } from 'common/utils/styles/flex-box';
import { strapiImageLoader } from 'common/utils/helpers/cms.helpers';

export const Footer: React.FC = () => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  const customerAffinity = useUserStore().customerAffinity;

  const { data: footerData, isLoading } = useCmsWithQuerySingle<FooterDetailsNode | null>(
    CmsCollections.footerDetails,
    CmsFieldNames.FOOTER_AFFINITY_CODE_FIELD,
    customerAffinity.affinityCode ? customerAffinity.affinityCode : 0
  );

  if (isLoading) {
    return (
      <FlexBox flexType="container" justify="center" align="center">
        <Loader data-testid="cms-banner-loader" />
      </FlexBox>
    );
  }

  if (footerData) {
    return (
      <>
        <FooterContainer id="lkf-footer-container">
          <FooterLogoContainer>
            <Image loader={strapiImageLoader} src={footerData.logo} alt="logo" width="184" height="33" layout="fixed" />
          </FooterLogoContainer>
          <FooterInnerContainer>
            <FooterInnerContainerContent>
              <FooterContentBlocks>
                {(footerData.addressLine1 || footerData.addressLine2) && (
                  <>
                    <SocialMediaSection>{footerData.addressTitle}</SocialMediaSection>
                    <AddressContent>
                      {footerData.addressLine1 && (
                        <>
                          {footerData.addressLine1}
                          <br />
                        </>
                      )}
                      {footerData.addressLine2}
                    </AddressContent>
                  </>
                )}
                {footerData.organizationNumber && (
                  <>
                    <SocialMediaSection>{footerData.organizationNumberTitle}</SocialMediaSection>
                    <AddressContent>{footerData.organizationNumber}</AddressContent>
                  </>
                )}
              </FooterContentBlocks>
              <FooterContentBlocks>
                {(footerData.email || footerData.phone) && (
                  <>
                    <SocialMediaSection>{footerData.contactTitle}</SocialMediaSection>
                    {footerData.phone && (
                      <FooterContainerItem>
                        <Image src={PhoneIcon} alt="phone" width={18} height={18} layout="fixed" />
                        <ContactDetailsItems>{footerData.phone}</ContactDetailsItems>
                      </FooterContainerItem>
                    )}
                    {footerData.email && (
                      <FooterContainerItem>
                        <Image src={EnvelopeIcon} alt="envelope" width={18} height={18} layout="fixed" />
                        <ContactDetailsItems>{footerData.email}</ContactDetailsItems>
                      </FooterContainerItem>
                    )}
                  </>
                )}
              </FooterContentBlocks>
            </FooterInnerContainerContent>
            <PrivacyAndPolicyContent>
              {t('footer.footerDeclaration')}
              <FooterExtLink href="https://www.finansportalen.no/" target="_blank">
                {t('footer.finansportalenLink')}{' '}
              </FooterExtLink>
              <br /> {t('footer.copyright')}
              {currentYear}
              {t('footer.landkreditt')}{' '}
              <FooterExtLink
                href="https://www.landkredittbank.no/vilkar-og-sikkerhet/rettigheter-og-vilkar/"
                target="_blank"
              >
                {t('footer.rights')}&nbsp;
              </FooterExtLink>
              &nbsp;
              {t('footer.conditions')}&nbsp;
              <FooterExtLink
                href="https://www.landkredittbank.no/vilkar-og-sikkerhet/guide-personvern-og-sikkerhet/"
                target="_blank"
              >
                {t('footer.privacy')}&nbsp;
              </FooterExtLink>{' '}
              {t('footer.security')}&nbsp;
              <FooterExtLink href="https://www.landkredittbank.no/vilkar-og-sikkerhet/cookies/" target="_blank">
                {t('footer.cookies')}.
              </FooterExtLink>
            </PrivacyAndPolicyContent>
          </FooterInnerContainer>
        </FooterContainer>
      </>
    );
  }

  return <></>;
};
